import { z } from 'zod';

import { MAX_SITE_DATABASES, siteDatabaseSpecSchema } from '../access/database';
import { backupRetentionSchema } from '../backup/policy';
import { snapshotIdSchema } from '../backup/snapshot';
import { resticPasswordSchema, resticRepositorySchema } from '../backup/storage-box';
import { isoDateTimeSchema, portSchema, uuidSchema } from '../common/primitives';
import { siteSpecSchema } from '../provisioning/site-spec';

/**
 * Provisionierungsbefehle in Richtung Master -> Worker.
 *
 * ## Warum der Agent sofort antwortet
 *
 * Jeder Aufruf wird mit `202 Accepted` beantwortet und die Arbeit läuft danach
 * asynchron weiter. Ein Provisioning kann Minuten dauern — allein der
 * Image-Pull. Keine HTTP-Verbindung sollte so lange offen stehen, und ein
 * Zeitüberschreitungsfehler auf der Master-Seite würde eine erfolgreiche
 * Operation als gescheitert melden.
 *
 * Der Master erfährt das Ergebnis deshalb nicht aus dieser Antwort, sondern aus
 * dem nächsten Heartbeat-Inventar (verbindlich) und aus der Rückmeldung des
 * Agents an `POST agent/v1/operations/:id/result` (nur zur Fehlerdiagnose).
 *
 * ## Warum jeder Befehl eine `operationId` trägt
 *
 * Die Queue wiederholt Aufrufe mit wachsendem Abstand. Ohne Idempotenz wäre
 * eine Wiederholung gefährlich; mit ihr ist sie kostenlos. Der Agent führt eine
 * ihm bekannte Operation nie ein zweites Mal aus, sondern gibt das gespeicherte
 * Ergebnis zurück.
 */

/**
 * Auf welche Storage Box sich das Repository bezieht.
 *
 * `null` heißt: ein lokales Repository, also der Weg, gegen den die
 * Integrationstests laufen. Für alles andere braucht der Agent die Kennung —
 * aus ihr **baut er selbst** den Pfad zum SSH-Schlüssel
 * (`/etc/kraaapf/storage/<kennung>.key`), den das Manifest dort abgelegt hat.
 *
 * Genau so und nicht als Pfad: Ein vom Master gelieferter Dateipfad wäre eine
 * Zeichenkette, die bestimmt, welche Datei der Agent liest. Eine UUID ist ein
 * Wert, aus dem der Agent einen Pfad bildet — dieselbe Trennung wie bei
 * `POST_INSTALL_ACTIONS` in M5.
 */
const storageBoxReferenceSchema = uuidSchema.nullable();

/**
 * Der SSH-Port der Box.
 *
 * Er steht im Auftrag und nicht in der Konfiguration des Workers: Eine
 * Installation kann mehrere Boxen haben, und die müssen nicht auf demselben
 * Port lauschen. Ein Wert je Worker wäre eine Zusage, die beim zweiten
 * Aufbewahrungsort still falsch wird.
 *
 * `null` bei einem lokalen Repository — dort gibt es keine Verbindung.
 */
const storagePortSchema = portSchema.nullable();

/**
 * Ein Repository auf einer Storage Box braucht die Kennung der Box.
 *
 * Ohne sie fände der Agent den Schlüssel nicht und liefe in einen
 * Verbindungsfehler, dessen Ursache nach einem Netzproblem aussieht. Der
 * Vertrag lehnt das vorher ab.
 */
function requireBoxForSftp(
  value: { repository: string; storageBoxId: string | null; storagePort: number | null },
  context: z.RefinementCtx,
): void {
  if (!value.repository.startsWith('sftp:')) {
    return;
  }

  if (value.storageBoxId === null) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['storageBoxId'],
      message:
        'Ein Repository auf einer Storage Box verlangt die Kennung der Box — aus ihr bildet der Agent den Pfad zum SSH-Schlüssel.',
    });
  }

  if (value.storagePort === null) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['storagePort'],
      message:
        'Ein Repository auf einer Storage Box verlangt ihren SSH-Port. Bei Hetzner ist das 23 und nicht 22.',
    });
  }
}

/** Rumpf von `POST /v1/sites`. */
export const applySiteRequestSchema = z.object({
  operationId: uuidSchema,
  spec: siteSpecSchema,
});

export type ApplySiteRequest = z.infer<typeof applySiteRequestSchema>;

/** Rumpf von `DELETE /v1/sites/:siteId`. */
export const removeSiteRequestSchema = z.object({
  operationId: uuidSchema,
});

export type RemoveSiteRequest = z.infer<typeof removeSiteRequestSchema>;

/**
 * Rumpf von `POST /v1/sites/:siteId/backup`.
 *
 * ## Warum das nicht in der Spezifikation steht
 *
 * Repository und Passwort gehören nicht in den Soll-Zustand des Stacks. Sie
 * stünden sonst im kanonischen Hash, und das Erneuern eines Passworts hieße
 * jedes Mal: neuer Hash, abweichende Site, Neuaufbau aller Container. Eine
 * Sicherung darf keine Website anfassen.
 *
 * ## Warum die Datenbanken mitreisen
 *
 * Der Agent hat keine Datenbank und keinen Zustandsspeicher außer den
 * Containern selbst (`app.module.ts`). Er kennt die Zugangsdaten der
 * Kundendatenbanken nur so lange, wie eine `apply`-Operation läuft. Wer
 * sichern will, muss sie mitschicken.
 */
export const backupSiteRequestSchema = z
  .object({
    operationId: uuidSchema,
    repository: resticRepositorySchema,
    password: resticPasswordSchema,
    storageBoxId: storageBoxReferenceSchema,
    storagePort: storagePortSchema,
    retention: backupRetentionSchema,
    /** Leer erlaubt: eine statische Site hat keine Datenbank. */
    databases: z.array(siteDatabaseSpecSchema).max(MAX_SITE_DATABASES).default([]),
  })
  .superRefine(requireBoxForSftp);

export type BackupSiteRequest = z.infer<typeof backupSiteRequestSchema>;

/**
 * Rumpf von `POST /v1/sites/:siteId/restore`.
 *
 * Der Agent hält den Stack an, **räumt den jetzigen Stand beiseite** statt ihn
 * zu überschreiben, stellt her und startet wieder. Der zweite Schritt ist der
 * wichtigste: Ein Rückweg, der das Vorhandene löscht, macht aus einem
 * Bedienfehler einen Datenverlust — und im Portal drückt diesen Knopf jemand
 * unter Druck.
 */
export const restoreSiteRequestSchema = z
  .object({
    operationId: uuidSchema,
    repository: resticRepositorySchema,
    password: resticPasswordSchema,
    storageBoxId: storageBoxReferenceSchema,
    storagePort: storagePortSchema,
    snapshotId: snapshotIdSchema,
    databases: z.array(siteDatabaseSpecSchema).max(MAX_SITE_DATABASES).default([]),
  })
  .superRefine(requireBoxForSftp);

export type RestoreSiteRequest = z.infer<typeof restoreSiteRequestSchema>;

/** Antwort aller vier Endpunkte: die Annahme, nicht das Ergebnis. */
export const siteCommandAcceptedSchema = z.object({
  operationId: uuidSchema,
  siteId: uuidSchema,
  acceptedAt: isoDateTimeSchema,
  /**
   * `true`, wenn der Agent diese Operation bereits kannte und nichts erneut
   * ausgeführt hat. Für den Master der Beweis, dass seine Wiederholung folgenlos
   * war — und im Log der Unterschied zwischen "hat funktioniert" und "hat
   * zweimal funktioniert".
   */
  alreadyKnown: z.boolean(),
});

export type SiteCommandAccepted = z.infer<typeof siteCommandAcceptedSchema>;
